import {
  GenericLayoutApiModel,
  LocalizedImageApiModel,
  LocalizedStringApiModel,
  MarketingPageItemButtonActionApiModel,
} from '@cxb/editor-stb-api';
import { PlatformType } from 'osn-shared';
import {
  CreateMarketingPageItemButtonApiModel,
  LocalizedPlatformImageApiModel,
  PlatformApiModel,
  PlatformImageApiModel,
} from '@cxb/editor-stb-api/model/models';
import { PageItemCommon } from '../../domain/page-item';
import {
  TrayWithCountryTargeting,
  TrayWithDisplayServiceTypeIcon,
  TrayWithDthStatusIds,
  TrayWithFullTargeting,
  TrayWithLayout,
  TrayWithLocalizedBackgroundImages,
  TrayWithLocalizedDescription,
  TrayWithLocalizedHeadlineText,
  TrayWithLocalizedTitle,
  TrayWithPrimaryButton,
  TrayWithScheduling,
  TrayWithUserTargeting,
} from '../../domain/tray-common';
import { TraySelectorType } from '../components/tray-selector/tray-selector.enum';
import { ImageData } from '../../domain/localized-domain-images';
import { MarketingItemLayout } from '../../domain/domain-layouts';
import { MarketingButtonAction } from '../../shared/enums/marketing.enums';
import { MarketingTrayButton, MarketingTrayButtonType } from '../../domain/marketing-tray-button';

const mapMarketingTrayApiModelButtonActionToDomainAction = (action: MarketingPageItemButtonActionApiModel): MarketingButtonAction => {
  switch (action) {
    case MarketingPageItemButtonActionApiModel.SignUp:
      return MarketingButtonAction.SIGN_UP;
    case MarketingPageItemButtonActionApiModel.LogIn:
      return MarketingButtonAction.SIGN_IN;
    case MarketingPageItemButtonActionApiModel.CxbPage:
      return MarketingButtonAction.INTERNAL_PAGE;
    case MarketingPageItemButtonActionApiModel.NewPassword:
      return MarketingButtonAction.CHOOSE_NEW_PASSWORD;
    case MarketingPageItemButtonActionApiModel.Content:
      return MarketingButtonAction.PROGRAM;
    case MarketingPageItemButtonActionApiModel.ExternalUrl:
      return MarketingButtonAction.EXTERNAL_URL;
    case MarketingPageItemButtonActionApiModel.CompleteSubscription:
      return MarketingButtonAction.RENEW_OR_COMPLETE_SUBSCRIPTION;
    default:
      return undefined;
  }
};

export const mapApiButtonToDomain = (
  button: CreateMarketingPageItemButtonApiModel,
  buttonType: MarketingTrayButtonType
): MarketingTrayButton => {
  return {
    buttonType,
    action: mapMarketingTrayApiModelButtonActionToDomainAction(button.action),
    buttonTextEnglish: button.labelLocalized.en,
    buttonTextArabic: button.labelLocalized.ar,
    buttonActionPageTargetId: button.targetPageId,
    buttonActionExternalUrl: button.externalUrl,
    buttonActionProgramTarget: button.mpxProgramId ? { id: parseInt(button.mpxProgramId, 10) } : undefined,
  };
};

export const mapApiCtaLayoutToDomain = (template: GenericLayoutApiModel): MarketingItemLayout => {
  switch (template) {
    case GenericLayoutApiModel.Left:
      return MarketingItemLayout.CTA_LEFT;
    case GenericLayoutApiModel.Right:
      return MarketingItemLayout.CTA_RIGHT;
  }
};

export const mapApiLayoutToDomain = (layout: GenericLayoutApiModel): TraySelectorType => {
  switch (layout) {
    case GenericLayoutApiModel.TwoCards:
      return TraySelectorType.LandscapeTwoCardsTray;
    case GenericLayoutApiModel.ThreeCards:
      return TraySelectorType.LandscapeThreeCardsTray;
    case GenericLayoutApiModel.FourCards:
      return TraySelectorType.LandscapeFourCardsTray;
    case GenericLayoutApiModel.Portrait:
      return TraySelectorType.PortraitLarge;
    default:
      return undefined;
  }
};

const stringDateToNumericDate = (stringDate: string): number | undefined => {
  return stringDate ? new Date(stringDate).getTime() : undefined;
};

export const scheduledDateMapperApiToDomain = <T extends { scheduledFrom?: string; scheduledTo?: string }>(tray: T): TrayWithScheduling => {
  return {
    scheduledFrom: stringDateToNumericDate(tray.scheduledFrom),
    scheduledTo: stringDateToNumericDate(tray.scheduledTo),
  };
};

export const dthStatusMapper = <T extends { dthStatusIds?: string[] }>(tray: T): TrayWithDthStatusIds => {
  return {
    dthSubStatusIds: tray.dthStatusIds,
  };
};

export const mapApiModelImageWithPlatformToDomain = (image: PlatformImageApiModel): ImageData => {
  return {
    id: image.id,
    name: image.originalName,
    publicUrl: image.publicUrl,
    platformId: image.platform.id,
    platformName: image.platform.name || PlatformType.Web,
  };
};

export const descriptionLocalizedMapperApiToDomain = <T extends { descriptionLocalized: LocalizedStringApiModel }>(
  tray: T
): TrayWithLocalizedDescription => {
  return {
    descriptionTextEnglish: tray.descriptionLocalized.en,
    descriptionTextArabic: tray.descriptionLocalized.ar,
  };
};

export const nameLocalizedMapperApiToDomain = <T extends { nameLocalized: LocalizedStringApiModel }>(tray: T): TrayWithLocalizedTitle => {
  return {
    titleEnglish: tray.nameLocalized.en,
    titleArabic: tray.nameLocalized.ar,
  };
};

const userTargetingMapper = <T extends { userSegmentIds: string[] }>(tray: T): TrayWithUserTargeting => {
  return {
    userSegmentIds: tray.userSegmentIds,
  };
};

const countryTargetingMapper = <T extends { countryIds: string[]; countryGroupIds: string[] }>(tray: T): TrayWithCountryTargeting => {
  return {
    countryIds: tray.countryIds,
    countryGroupIds: tray.countryGroupIds,
  };
};

export const targetingMapperApiToDomain = <T extends { userSegmentIds: string[]; countryIds: string[]; countryGroupIds: string[] }>(
  tray: T
): TrayWithFullTargeting => {
  return {
    ...userTargetingMapper(tray),
    ...countryTargetingMapper(tray),
  };
};

export const layoutMapperApiToDomain = <T extends { layout: GenericLayoutApiModel }>(tray: T): TrayWithLayout => {
  return {
    layout: mapApiLayoutToDomain(tray.layout),
  };
};

export const displayServiceTypeIconMapperApiToDomain = <T extends { displayServiceTypeIcon?: boolean }>(
  tray: T
): TrayWithDisplayServiceTypeIcon => {
  return {
    displayServiceTypeIcon: tray.displayServiceTypeIcon,
  };
};

export const headlineTextMapper = <T extends { titleLocalized: LocalizedStringApiModel }>(tray: T): TrayWithLocalizedHeadlineText => {
  return {
    headlineTextArabic: tray.titleLocalized.ar,
    headlineTextEnglish: tray.titleLocalized.en,
  };
};

export const buttonMapperApiToDomain = <T extends { button: CreateMarketingPageItemButtonApiModel }>(tray: T): TrayWithPrimaryButton => {
  return {
    primaryButton: mapApiButtonToDomain(tray.button, MarketingTrayButtonType.Primary),
  };
};

export const backgroundImagesMapperApiToDomain = <T extends { backgroundImages: LocalizedPlatformImageApiModel }>(
  tray: T
): TrayWithLocalizedBackgroundImages => {
  return {
    backgroundImages: {
      en: tray.backgroundImages.en.map(mapApiModelImageWithPlatformToDomain),
      ar: tray.backgroundImages.ar.map(mapApiModelImageWithPlatformToDomain),
    },
  };
};

export const commonMapper = <T extends { id: string; platforms?: PlatformApiModel[]; platformIds?: string[] }>(
  tray: T
): Pick<PageItemCommon, 'id' | 'order' | 'visible' | 'draggable' | 'platformIds'> => {
  return {
    id: tray.id,
    order: undefined,
    visible: undefined,
    draggable: undefined,
    platformIds: tray.platforms?.map((it) => it.id) ?? tray.platformIds ?? [],
  };
};

export const mapApiModelImageToDomain = <T extends LocalizedImageApiModel>(image: T): ImageData => {
  return {
    id: image.id,
    name: image.originalName,
    publicUrl: image.publicUrl,
  };
};
